import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Popconfirm, Popover, Tag, Typography, message } from 'antd';
import {
  CheckCircleOutlined,
  CoffeeOutlined,
  PauseCircleOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { useAccess, useModel } from '@umijs/max';
import { clockOut, getClockStatus } from '@/services/app/tenant/csr-clock';
import {
  acknowledgeBreakPrompt,
  agentStatusErrorMessage,
  formatStateElapsed,
  getMyAgentStatus,
  onAgentStatusUpdated,
  setMyAgentStatus,
  STATE_COLORS,
  STATE_HEX,
  STATE_LABELS,
  type AgentStatusMePayload,
  type BreakCode,
  type SetAgentStatusPayload,
} from '@/services/app/tenant/agent-status';
import { dtFormat } from '@/util';
import styles from './index.less';

const { Text } = Typography;

// GET /me is cheap; the WS push covers the instant case, the poll covers a
// missed push (and keeps `seconds_in_state` from drifting on long sessions).
const STATUS_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Nav-bar agent status pill (Available / Not Ready · Lunch / On Call / …)
 * with a live timer and a picker popover — self-service, CSRs only, and only
 * while the tenant has the Agent Status feature enabled.
 */
const AgentStatusWidget: React.FC = () => {
  const access = useAccess();
  const { initialState } = useModel('@@initialState');
  const currentUserId = initialState?.currentUser?.id;
  const isCsr = !!access?.isCurrentUserCsr;

  const [payload, setPayload] = useState<AgentStatusMePayload | null>(null);
  // Timers run off `seconds_in_state` + local elapsed since the fetch, so the
  // browser clock never has to agree with the server's.
  const [fetchedAt, setFetchedAt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [breakModal, setBreakModal] = useState<BreakCode | null>(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  // The prompt key (next_prompt_at) the user last answered — keeps the modal
  // from re-opening for the same prompt while the backend catches up.
  const answeredPromptKey = useRef<string | null>(null);
  const [promptBusy, setPromptBusy] = useState<'available' | 'still' | null>(null);

  const apply = useCallback((next: AgentStatusMePayload) => {
    setPayload(next);
    setFetchedAt(Date.now());
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await getMyAgentStatus());
    } catch {
      // Best-effort — the widget keeps whatever state it had.
    } finally {
      setLoaded(true);
    }
  }, [apply]);

  useEffect(() => {
    if (!isCsr) return;
    refresh();
    const poll = window.setInterval(refresh, STATUS_POLL_INTERVAL_MS);
    return () => window.clearInterval(poll);
  }, [isCsr, refresh]);

  // WS push: only our own row matters here. A push with no user_id is the
  // reconnect/coalesced signal — treat it as "refresh to be safe".
  useEffect(() => {
    if (!isCsr) return;
    return onAgentStatusUpdated((data) => {
      if (!data || data.user_id == null || data.user_id === currentUserId) refresh();
    });
  }, [isCsr, currentUserId, refresh]);

  const enabled = !!payload?.config?.enabled;

  // Tick once a second so the elapsed label keeps up without refetching.
  useEffect(() => {
    if (!isCsr || !enabled) return;
    const timer = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isCsr, enabled]);

  if (!isCsr || !loaded || !payload || !enabled) return null;

  const { status, config } = payload;
  const elapsedSeconds = status.seconds_in_state + Math.floor((Date.now() - fetchedAt) / 1000);
  const stateLabel = status.state_label || STATE_LABELS[status.state] || status.state;
  const breakCodes = (config.break_codes ?? []).filter((c) => c.is_active);
  const isDerivedState = status.state === 'on_call' || status.state === 'acw';
  const pendingBreakHint =
    isDerivedState && status.selected_state === 'not_ready'
      ? `${status.selected_break_label ?? 'Break'} starts after the ${status.state === 'on_call' ? 'call' : 'wrap-up'}`
      : null;

  const promptKey = status.break_prompt?.next_prompt_at ?? status.since;
  const promptOpen = !!status.break_prompt?.due && answeredPromptKey.current !== promptKey;
  const anyModalOpen = promptOpen || !!breakModal;

  const submitState = async (data: SetAgentStatusPayload, successText?: string) => {
    setBusy(true);
    try {
      apply(await setMyAgentStatus(data));
      if (successText) message.success(successText);
      return true;
    } catch (e: any) {
      message.error(agentStatusErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAvailable = async () => {
    if (await submitState({ state: 'available' }, 'You are now Available')) setPopoverOpen(false);
  };

  const openBreakModal = (code: BreakCode) => {
    setNote('');
    setNoteError(null);
    setBreakModal(code);
    setPopoverOpen(false);
  };

  const handleBreakConfirm = async () => {
    if (!breakModal) return;
    const trimmed = note.trim();
    if (breakModal.requires_note && !trimmed) {
      setNoteError('A note is required for this break');
      return;
    }
    const ok = await submitState(
      { state: 'not_ready', break_code: breakModal.code, note: trimmed || null },
      isDerivedState ? `${breakModal.label} will start after the ${status.state === 'on_call' ? 'call' : 'wrap-up'}` : `On break: ${breakModal.label}`,
    );
    if (ok) setBreakModal(null);
  };

  // Clocking out on the backend flips the status to offline; when the CSR is
  // not on the clock there is nothing to clock out of, so set the state directly.
  const handleOffline = async () => {
    setBusy(true);
    try {
      const clock = await getClockStatus().catch(() => null);
      if (clock?.clocked_in) {
        try {
          await clockOut();
          message.success('Clocked out');
        } catch {
          // The global request handler already toasted the server message.
          return;
        }
      } else {
        try {
          await setMyAgentStatus({ state: 'offline' });
        } catch (e: any) {
          message.error(agentStatusErrorMessage(e, 'Failed to go offline'));
          return;
        }
      }
      setPopoverOpen(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handlePromptBackAvailable = async () => {
    answeredPromptKey.current = promptKey;
    setPromptBusy('available');
    try {
      await submitState({ state: 'available' }, 'Welcome back — you are Available');
    } finally {
      setPromptBusy(null);
    }
  };

  const handlePromptStillOnBreak = async () => {
    answeredPromptKey.current = promptKey;
    setPromptBusy('still');
    try {
      apply(await acknowledgeBreakPrompt());
    } catch (e: any) {
      message.error(agentStatusErrorMessage(e, 'Failed to extend the break'));
      refresh();
    } finally {
      setPromptBusy(null);
    }
  };

  const breakPromptModal = (
    <Modal
      open={promptOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={392}
      destroyOnHidden
      zIndex={1050}
      title="Still on break?"
      styles={{ body: { padding: '20px 24px 24px' } }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#fff7e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <CoffeeOutlined style={{ fontSize: 26, color: '#fa8c16' }} />
        </div>
        <div style={{ fontSize: 15, marginBottom: 24 }}>
          You have been on <strong>{status.break_label ?? status.selected_break_label ?? 'break'}</strong> for{' '}
          <strong>{status.break_prompt?.minutes_on_break ?? Math.floor(elapsedSeconds / 60)} minutes</strong>.
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button loading={promptBusy === 'still'} disabled={promptBusy === 'available'} onClick={handlePromptStillOnBreak}>
            Still on break
          </Button>
          <Button
            type="primary"
            loading={promptBusy === 'available'}
            disabled={promptBusy === 'still'}
            onClick={handlePromptBackAvailable}
          >
            I&apos;m back — Available
          </Button>
        </div>
      </div>
    </Modal>
  );

  const breakNoteModal = (
    <Modal
      open={!!breakModal}
      title={breakModal ? `Not Ready — ${breakModal.label}` : 'Not Ready'}
      onCancel={() => setBreakModal(null)}
      onOk={handleBreakConfirm}
      okText={isDerivedState ? 'Set break' : 'Start break'}
      confirmLoading={busy}
      destroyOnHidden
      maskClosable={false}
      width={420}
      zIndex={1050}
    >
      {isDerivedState && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          You are currently {stateLabel.toLowerCase()} — the break starts once the{' '}
          {status.state === 'on_call' ? 'call' : 'wrap-up'} ends.
        </Text>
      )}
      <div style={{ marginBottom: 6 }}>
        Note{breakModal?.requires_note ? <span style={{ color: '#ff4d4f' }}> *</span> : <Text type="secondary"> (optional)</Text>}
      </div>
      <Input.TextArea
        rows={3}
        maxLength={500}
        value={note}
        status={noteError ? 'error' : undefined}
        placeholder={breakModal?.requires_note ? 'Why are you stepping away?' : 'Anything your supervisor should know'}
        onChange={(e) => {
          setNote(e.target.value);
          if (noteError) setNoteError(null);
        }}
        onPressEnter={(e) => {
          if (e.ctrlKey || e.metaKey) handleBreakConfirm();
        }}
      />
      {noteError && (
        <Text type="danger" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          {noteError}
        </Text>
      )}
    </Modal>
  );

  const currentBreakCode = status.selected_state === 'not_ready' ? status.selected_break_code : null;
  const availableSelected = status.state === 'available' || (isDerivedState && status.selected_state === 'available');

  const popoverContent = (
    <div style={{ width: 240 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `${STATE_HEX[status.state]}1a`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: STATE_HEX[status.state],
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {status.state === 'available' ? (
            <CheckCircleOutlined />
          ) : status.state === 'not_ready' ? (
            <CoffeeOutlined />
          ) : status.state === 'offline' ? (
            <PoweroffOutlined />
          ) : (
            <PauseCircleOutlined />
          )}
        </div>
        <div style={{ lineHeight: 1.4, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'rgba(0, 0, 0, 0.88)' }}>
            {stateLabel}
            {status.state === 'not_ready' && status.break_label ? ` · ${status.break_label}` : ''}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {formatStateElapsed(elapsedSeconds)} · since {dtFormat(status.since, 'h:mm A')}
          </div>
        </div>
      </div>

      {pendingBreakHint && (
        <div
          style={{
            fontSize: 12,
            color: '#ad6800',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 6,
            padding: '6px 8px',
            marginBottom: 12,
          }}
        >
          {pendingBreakHint}
        </div>
      )}

      <Button
        block
        type={availableSelected ? 'default' : 'primary'}
        disabled={availableSelected && !isDerivedState}
        loading={busy}
        icon={<CheckCircleOutlined />}
        onClick={handleAvailable}
        style={{ marginBottom: 12 }}
      >
        Available
      </Button>

      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>Not Ready</div>
      {breakCodes.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
          No break codes configured.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
          {breakCodes.map((code) => {
            const active = currentBreakCode === code.code;
            return (
              <Button
                key={code.id}
                block
                size="small"
                type={active ? 'primary' : 'text'}
                ghost={active}
                disabled={busy}
                onClick={() => openBreakModal(code)}
                style={{ justifyContent: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: code.color || '#fa8c16',
                    flexShrink: 0,
                    display: 'inline-block',
                  }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{code.label}</span>
                {code.requires_note && (
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8c8c8c' }}>note</span>
                )}
              </Button>
            );
          })}
        </div>
      )}

      <Popconfirm
        title="Go offline?"
        description="You will stop receiving calls and be clocked out."
        okText="Go offline"
        okButtonProps={{ danger: true }}
        onConfirm={handleOffline}
        disabled={status.state === 'offline'}
        zIndex={1060}
      >
        <Button block danger disabled={status.state === 'offline'} loading={busy} icon={<PoweroffOutlined />}>
          Offline
        </Button>
      </Popconfirm>
    </div>
  );

  const pillLabel =
    status.state === 'not_ready' && status.break_label ? `${stateLabel} · ${status.break_label}` : stateLabel;

  return (
    <>
      <Popover
        content={popoverContent}
        trigger={['click']}
        open={!anyModalOpen && popoverOpen}
        onOpenChange={(next) => {
          if (!anyModalOpen) setPopoverOpen(next);
        }}
        placement="bottomRight"
        styles={{ body: { padding: 16, borderRadius: 12 } }}
      >
        <span className={styles.action} role="button" aria-label={`Agent status: ${pillLabel}`}>
          <Tag
            color={STATE_COLORS[status.state]}
            style={{
              marginInlineEnd: 0,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              lineHeight: '20px',
              maxWidth: 260,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pillLabel}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>{formatStateElapsed(elapsedSeconds)}</span>
          </Tag>
        </span>
      </Popover>
      {breakNoteModal}
      {breakPromptModal}
    </>
  );
};

export default AgentStatusWidget;
